import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

export interface PageMetaProps {
  /** Page-specific title. Will be appended with the site name unless `titleOverride` is true. */
  title: string;
  /** Concise description for search results and social cards. Aim for ~155 chars. */
  description: string;
  /** Absolute or root-relative path to the share image. Defaults to /og/default.jpg. */
  image?: string;
  /**
   * Optional canonical path (e.g. "/visit"). If omitted, the current pathname
   * is used.
   */
  canonicalPath?: string;
  /** When true, render `title` exactly as given (no site-name suffix). */
  titleOverride?: boolean;
  /** Open Graph type. Defaults to "website". */
  ogType?: 'website' | 'article' | 'museum' | 'profile';
  /** Optional JSON-LD object that will be embedded in the head. */
  jsonLd?: Record<string, unknown>;
  /** Children Helmet tags to render alongside (e.g. extra meta or links). */
  children?: React.ReactNode;
}

const SITE_NAME = 'Y.O. Ranch Headquarters Texas History Museum';
const SITE_ORIGIN = 'https://yoranch.lovable.app';
const DEFAULT_IMAGE = '/og/default.jpg';

const toAbsolute = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Renders a consistent set of <title>, description, canonical, Open Graph,
 * and Twitter Card tags for any page. Wrap the page in a single `<PageMeta />`
 * call near the top of its return tree.
 */
const PageMeta = ({
  title,
  description,
  image,
  canonicalPath,
  titleOverride,
  ogType = 'website',
  jsonLd,
  children,
}: PageMetaProps) => {
  const location = useLocation();
  const path = canonicalPath ?? location.pathname;
  const canonical = toAbsolute(path);
  const fullTitle = titleOverride ? title : `${title} | ${SITE_NAME}`;
  const shareImage = toAbsolute(image ?? DEFAULT_IMAGE);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={shareImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={shareImage} />

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}

      {children}
    </Helmet>
  );
};

export default PageMeta;
